import { useEffect, useState, useRef } from 'react';
import { io } from 'socket.io-client';
import { API_ORIGIN } from '../services/api';

const useKitchenSocket = (restaurantId, stationIds, callbacks = {}) => {
  const [connected, setConnected] = useState(false);
  const socketRef = useRef(null);
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;
  // Distinguishes the first connect from later reconnects.
  const hasConnectedRef = useRef(false);

  useEffect(() => {
    const socket = io(API_ORIGIN, {
      transports: ['websocket'],
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      withCredentials: true // send the httpOnly auth cookie on the handshake
    });
    socketRef.current = socket;

    socket.on('connect_error', (err) => {
      console.warn('Kitchen socket connection rejected:', err.message);
    });

    // Surface transport/engine errors instead of swallowing them silently.
    socket.on('error', (err) => {
      console.error('Kitchen socket error:', err && err.message ? err.message : err);
    });

    // After reconnectionAttempts is exhausted the client stops trying and goes
    // silent — mark the socket disconnected and notify the page so it can show a
    // stale-data warning / fall back to polling. This listener lives on the
    // Manager (socket.io), which can be reused across remounts, so it is removed
    // explicitly in cleanup below.
    const handleReconnectFailed = () => {
      console.error('Kitchen socket gave up reconnecting');
      setConnected(false);
      if (callbacksRef.current.onReconnectFailed) {
        callbacksRef.current.onReconnectFailed();
      }
    };
    socket.io.on('reconnect_failed', handleReconnectFailed);

    socket.on('connect', () => {
      setConnected(true);
      if (restaurantId) {
        socket.emit('join_restaurant', restaurantId);
      }
      if (stationIds && stationIds.length > 0) {
        stationIds.forEach((stationId) => {
          socket.emit('join_station', { restaurantId, stationId });
        });
      }
      // A reconnect means we may have missed events while offline — let the
      // page resync. The first connect is covered by the initial fetch.
      if (hasConnectedRef.current && callbacksRef.current.onReconnect) {
        callbacksRef.current.onReconnect();
      }
      hasConnectedRef.current = true;
    });

    socket.on('disconnect', () => {
      setConnected(false);
    });

    socket.on('new_order', (order) => {
      if (callbacksRef.current.onNewOrder) callbacksRef.current.onNewOrder(order);
    });

    socket.on('order_status_updated', (data) => {
      if (callbacksRef.current.onOrderUpdate) callbacksRef.current.onOrderUpdate(data);
    });

    socket.on('order_item_updated', (data) => {
      if (callbacksRef.current.onItemUpdate) callbacksRef.current.onItemUpdate(data);
    });

    socket.on('order_bumped', (data) => {
      if (callbacksRef.current.onOrderBumped) callbacksRef.current.onOrderBumped(data);
    });

    socket.on('order_recalled', (data) => {
      if (callbacksRef.current.onOrderRecalled) callbacksRef.current.onOrderRecalled(data);
    });

    return () => {
      socket.io.off('reconnect_failed', handleReconnectFailed);
      socket.disconnect();
      socketRef.current = null;
      hasConnectedRef.current = false;
    };
  }, [restaurantId, stationIds && stationIds.join(',')]);

  return { socket: socketRef.current, connected };
};

export default useKitchenSocket;
